import { RefObject, useEffect, useState } from 'react';

const MATERIALS_DURATION = 350;
const PROCESSING_DURATION = 350;
const CHARACTER_DURATION = 22;
const TICK = 28;

/** Brief preparation steps lead into typing; the result completes with the final character. */
const useProtocolPlayback = (
  containerRef: RefObject<HTMLElement>,
  active: boolean,
  characterCount: number,
  replay: number,
) => {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const answerStart = MATERIALS_DURATION + PROCESSING_DURATION;
  const duration = answerStart + characterCount * CHARACTER_DURATION;

  useEffect(() => {
    const container = containerRef.current;
    setElapsed(0);
    setPlaying(false);
    if (!active || !container) {
      return undefined;
    }

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let position = 0;
    let inView = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const stop = () => {
      clearInterval(timer);
      timer = undefined;
    };
    const update = () => {
      stop();
      if (motion.matches) {
        position = duration;
        setElapsed(position);
      }
      const shouldPlay = inView && !document.hidden && position < duration;
      setPlaying(shouldPlay);
      if (!shouldPlay) {
        return;
      }
      timer = setInterval(() => {
        position = Math.min(position + TICK, duration);
        setElapsed(position);
        if (position === duration) {
          stop();
          setPlaying(false);
        }
      }, TICK);
    };
    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      update();
    }, { threshold: 0.15 });

    observer.observe(container);
    motion.addEventListener('change', update);
    document.addEventListener('visibilitychange', update);
    update();

    return () => {
      stop();
      observer.disconnect();
      motion.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [active, containerRef, duration, replay]);

  const position = active ? elapsed : duration;
  let stage = 0;
  if (position >= duration) {
    stage = 3;
  } else if (position >= answerStart) {
    stage = 2;
  } else if (position >= MATERIALS_DURATION) {
    stage = 1;
  }

  return {
    stage,
    playing,
    length: Math.min(characterCount, Math.max(0, Math.floor((position - answerStart) / CHARACTER_DURATION))),
  };
};

export default useProtocolPlayback;
